import {
  ActionType as ApiActionType,
  ActionSubtype,
  type Integration,
  type NodeActionGroup,
  type SaveWorkflowActionDto,
  type WorkflowActionContactInput,
} from '@super/shared-types'
import type {
  ActionRow,
  ActionRowType,
  PushOption,
  TransferSettings,
} from '@/components/actions/ActionEditor'
import type {
  ContactActionType,
  RoleWithMembers,
  Staff,
} from '@/lib/api/directory.client'
import {
  contactChannelOf,
  editorToWorkflowGroups,
  workflowToEditorGroups,
} from '@/lib/handoff-rule.converters'
import { parseMentions } from '@/lib/handoff-mentions'

/** Author-local row state the inspector tracks between hydration and save.
 *  `workflowActionId` is the anchor catalog row for an existing recipient
 *  set; null means the row was added locally and will create new rows on
 *  first save. */
export interface RowState {
  localId: string
  workflowActionId: string | null
  row: ActionRow
}

export interface BuildDtosContext {
  /** Synthetic group id passed to `editorToWorkflowGroups`. Any stable
   *  string works; the converter uses it only to key its internal maps. */
  groupId: string
  roles: RoleWithMembers[]
  staff: Staff[]
  availableIntegrations: Integration[]
}

/**
 * Expand one editor row into the catalog DTOs it represents. A row with
 * multiple recipients ("jim@x.com, @leasing") produces one DTO per
 * recipient — matching how v2's handoff rules and v3's catalog detail
 * page both fan out. The caller sends them to `bulkSave`: the first DTO
 * updates the anchor row in place, the rest create siblings.
 *
 * Pure. All inputs come from the arguments; no React or network state.
 */
export function buildCatalogDtosForRow(
  row: ActionRow,
  transfer: TransferSettings,
  ctx: BuildDtosContext,
): {
  dtos: SaveWorkflowActionDto[]
  actionTypeForMember: ApiActionType | null
} {
  const { pairs } = editorToWorkflowGroups(
    [
      {
        id: ctx.groupId,
        name: '',
        when: '',
        actions: [row],
        transfer: row.type === 'TRANSFER' ? transfer : undefined,
      },
    ],
    ctx.roles,
    ctx.staff,
    ctx.availableIntegrations,
  )
  const apiActions = pairs[0]?.api.actions ?? []
  if (apiActions.length === 0) {
    return { dtos: [], actionTypeForMember: null }
  }
  const channel: ContactActionType | null =
    row.type === 'PUSH_INTEGRATION' ? null : contactChannelOf(row.type)
  const parsedMentions =
    row.type === 'PUSH_INTEGRATION'
      ? { roleIds: new Set<string>(), staffIds: new Set<string>() }
      : parseMentions(row.target, ctx.roles, ctx.staff)
  const contacts: WorkflowActionContactInput[] = [
    ...[...parsedMentions.roleIds].map(roleId => ({
      roleId,
      actionType: channel ?? null,
    })),
    ...[...parsedMentions.staffIds].map(staffId => ({
      staffId,
      actionType: channel ?? null,
    })),
  ]
  let actionTypeForMember: ApiActionType | null = null
  const dtos: SaveWorkflowActionDto[] = []
  for (const apiAction of apiActions) {
    const resolvedType =
      apiAction.actionDefinition?.actionType ?? apiAction.actionType
    if (!resolvedType) continue
    if (!actionTypeForMember) {
      actionTypeForMember = resolvedType as ApiActionType
    }
    dtos.push({
      name: null,
      description: null,
      actionType: resolvedType as ApiActionType,
      actionSubtype: (apiAction.actionSubtype ?? null) as ActionSubtype | null,
      parameters: (apiAction.parameters ?? null) as Record<
        string,
        unknown
      > | null,
      integrationId: apiAction.integrationId ?? null,
      contacts,
    })
  }
  return { dtos, actionTypeForMember }
}

export interface HydrateMembersContext {
  pushOptions: PushOption[]
  roles: RoleWithMembers[]
  staff: Staff[]
}

/**
 * Collapse a group's catalog members back into editor rows. Multiple
 * same-type catalog rows (one per recipient of the same email action) fold
 * into one editor row with a multi-recipient target.
 *
 * Each resulting row is anchored to the first unused catalog member of
 * the matching type. On save, `bulkSave(anchorId, dtos)` updates the
 * anchor in place and creates sibling rows for extra recipients, so the
 * identity of the anchor survives a round-trip.
 *
 * Pure. Returns `[]` when the group has no members, which the inspector
 * then replaces with a blank starter row in draft mode.
 */
export function hydrateMembersToRowStates(
  group: NodeActionGroup,
  ctx: HydrateMembersContext,
): RowState[] {
  const members = group.members ?? []
  if (members.length === 0) return []
  const editorGroups = workflowToEditorGroups(
    [
      {
        id: group.id,
        name: null,
        selectionType: 'CONDITIONAL',
        selectionCriteria: null,
        actions: members.map(m => ({
          actionDefinition: { actionType: m.workflowAction.actionType },
          actionType: m.workflowAction.actionType,
          actionSubtype: (m.workflowAction.actionSubtype ??
            null) as ActionSubtype | null,
          parameters: (m.workflowAction.parameters ?? null) as Record<
            string,
            unknown
          > | null,
          integrationId: m.workflowAction.integrationId,
        })),
      },
    ],
    ctx.pushOptions,
    {
      contactsByGroupId: new Map([
        [
          group.id,
          members.flatMap(m =>
            (m.workflowAction.contacts ?? []).map(c => ({
              id: c.id,
              actionGroupId: group.id,
              actionType: c.actionType as ContactActionType | null,
              roleId: c.roleId,
              staffId: c.staffId,
              role: null,
              staff: null,
              createdAt: '',
            })),
          ),
        ],
      ]),
      roles: ctx.roles,
      staff: ctx.staff,
    },
  )
  const editorRows = editorGroups[0]?.actions ?? []
  if (editorRows.length === 0) return []
  const anchorsUsed = new Set<string>()
  return editorRows.map(editorRow => {
    const match = members.find(
      m =>
        !anchorsUsed.has(m.workflowActionId) &&
        (m.workflowAction.actionType as string) ===
          (editorRow.type as ActionRowType),
    )
    const anchorId = match?.workflowActionId ?? members[0]!.workflowActionId
    if (match) anchorsUsed.add(match.workflowActionId)
    return {
      localId: anchorId,
      workflowActionId: anchorId,
      row: editorRow,
    }
  })
}
